import type {
  ApiPhysicalQuantitiesType,
  ApiProjectSitesType,
  ApiReturnAction,
  ApiReturnAllSensor,
  ApiReturnCampaign,
  ApiReturnCompany,
  ApiReturnDeviceMeasureType,
  ApiReturnDeviceSite,
  ApiReturnDeviceType,
  ApiReturnDeviceTypeMeasureType,
  ApiReturnMeasurePoint,
  ApiReturnMeasuresValidated,
  ApiReturnMeasureType,
  ApiReturnProject,
  ApiReturnResource,
  ApiReturnResourcesTags,
  ApiReturnRole,
  ApiReturnSite,
  ApiReturnSiteIdsToDashboardIds,
  ApiReturnTag,
  ApiReturnThematic,
  ApiReturnUser,
} from '@highimpact-frontend/api-types';
import { keysMatching } from '../../utils/type-generators';
import type {
  ConfigTableByParams,
  ConfigTableType,
  PermittedAction,
} from './data';
import type { DataState, State } from './state';

export type CachedDataState<T> =
  | { _tag: 'idle'; type: 'cached'; id: string }
  | { _tag: 'loading'; type: 'cached'; id: string }
  | { _tag: 'error'; error: string; type: 'cached'; id: string }
  | { _tag: 'data'; data: T; type: 'cached'; id: string };

export interface DataStoreType {
  externals: {
    authentication: State<string, ApiReturnUser>;

    users: ConfigTableType<ApiReturnUser>;
    roles: ConfigTableType<ApiReturnRole>;
    projects: ConfigTableType<ApiReturnProject>;
    sites: ConfigTableType<ApiReturnSite>;
    actions: ConfigTableType<ApiReturnAction>;
    devices: ConfigTableType<ApiReturnAllSensor>;
    measure_types: ConfigTableType<ApiReturnMeasureType>;
    measure_points: ConfigTableType<ApiReturnMeasurePoint>;
    device_types: ConfigTableType<ApiReturnDeviceType>;
    thematics: ConfigTableType<ApiReturnThematic>;

    project_sites: ConfigTableType<ApiProjectSitesType>;
    site_devices: ConfigTableType<ApiReturnDeviceSite>;
    device_measure_types: ConfigTableType<ApiReturnDeviceMeasureType>;
    device_type_measure_types: ConfigTableType<ApiReturnDeviceTypeMeasureType>;
    resources_tags: ConfigTableType<ApiReturnResourcesTags>;
    tags: ConfigTableType<ApiReturnTag>;

    campaigns: ConfigTableByParams<ApiReturnCampaign>;
    companies: ConfigTableType<ApiReturnCompany>;
    physical_quantities: State<string, ApiPhysicalQuantitiesType>;

    siteIdsToDashboardIds: State<string, ApiReturnSiteIdsToDashboardIds>;
  };
  credentials: {
    permissions: {
      [K in keyof DataStoreType['externals']]?: State<string, PermittedAction>;
    };
  };

  resources: { [KEY in number]: State<number, ApiReturnResource> };

  /**
   * @description associates a full path + search params to a data state
   * @example
   * {
   *   '/path/to/endpoint?param1=abc&param2=def': {
   *     _tag: 'idle',
   *     type: 'datasetsByParams',
   *     id: '/path/to/endpoint?param1=abc&param2=def'
   *   }
   * }
   */
  datasetsByParams: Record<string, CachedDataState<ApiReturnMeasuresValidated>>;
}

export type ExternalsWithParams = keysMatching<
  DataStoreType['externals'],
  ConfigTableByParams<object>
>;
export type ExternalWithoutParams = keyof Omit<
  DataStoreType['externals'],
  ExternalsWithParams
>;
type ExternalsToParamType = {
  campaigns: { site_id: string };
};

export const paramKeyGetter: Record<
  ExternalsWithParams,
  {
    makeParamKey: (params: ExternalsToParamType[ExternalsWithParams]) => string;
  }
> = {
  campaigns: { makeParamKey: (params) => `${params.site_id}` },
};

/**
 * DataStoreTypes.
 */
export type ExternalNameTypes = keyof DataStoreType['externals'];

/**
 * Retrieve's only data in "data" state.
 */
export type GetDataState<T> = T extends DataState<infer K, infer U>
  ? DataState<K, U>
  : never;

/**
 * Select a DataStoreType.
 */
export type ExternalTypeFromName<T extends ExternalNameTypes> = GetDataState<
  DataStoreType['externals'][T]
>;

export const initials: DataStoreType = {
  credentials: {
    permissions: {},
  },

  externals: {
    authentication: { _tag: 'idle', type: 'externals', id: 'authentication' },

    users: { _tag: 'idle', type: 'externals', id: 'users' },
    roles: { _tag: 'idle', type: 'externals', id: 'roles' },
    projects: { _tag: 'idle', type: 'externals', id: 'projects' },
    sites: { _tag: 'idle', type: 'externals', id: 'sites' },
    actions: { _tag: 'idle', type: 'externals', id: 'actions' },
    devices: { _tag: 'idle', type: 'externals', id: 'devices' },
    measure_types: { _tag: 'idle', type: 'externals', id: 'measure_types' },
    measure_points: { _tag: 'idle', type: 'externals', id: 'measure_points' },
    device_types: { _tag: 'idle', type: 'externals', id: 'device_types' },
    thematics: { _tag: 'idle', type: 'externals', id: 'thematics' },
    campaigns: { type: 'ByParams', value: {}, id: 'campaigns' },
    companies: { _tag: 'idle', type: 'externals', id: 'companies' },
    project_sites: { _tag: 'idle', type: 'externals', id: 'project_sites' },
    site_devices: { _tag: 'idle', type: 'externals', id: 'site_devices' },
    resources_tags: { _tag: 'idle', type: 'externals', id: 'resources_tags' },
    tags: { _tag: 'idle', type: 'externals', id: 'tags' },

    device_measure_types: {
      _tag: 'idle',
      type: 'externals',
      id: 'device_measure_types',
    },
    device_type_measure_types: {
      _tag: 'idle',
      type: 'externals',
      id: 'device_type_measure_types',
    },

    physical_quantities: {
      _tag: 'idle',
      type: 'externals',
      id: 'physical_quantities',
    },

    siteIdsToDashboardIds: {
      _tag: 'idle',
      type: 'externals',
      id: 'siteIdsToDashboardIds',
    },
  },

  datasetsByParams: {},

  resources: {},
};
